import React, { useEffect, useState } from "react";
import { Trash2, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { apiError, isSuperAdmin } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";

const STATUS = {
  en_revision: { label: "En revisión", cls: "bg-[#FBF0CE] text-[#B9860B]", icon: Clock },
  valida: { label: "Válida", cls: "bg-[#E2F6EB] text-[#22A860]", icon: CheckCircle2 },
  rechazada: { label: "Rechazada", cls: "bg-[#FCE4E4] text-[#E23B3B]", icon: XCircle },
};

export function StatusBadge({ status }) {
  const st = STATUS[status] || STATUS.en_revision;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${st.cls}`}>
      <st.icon className="w-3.5 h-3.5" /> {st.label}
    </span>
  );
}

const DEFAULT_FILTERS = [
  { key: "all", label: "Todas" },
  { key: "en_revision", label: "En revisión" },
  { key: "valida", label: "Válidas" },
  { key: "rechazada", label: "Rechazadas" },
];

// Panel reutilizable para listar y ELIMINAR (solo Super Admin) evidencias/registros
// preservando los puntos del usuario. `fetchList(filter)`, `deleteOne(id)`, `deleteBulk(payload)`.
export default function EvidenciaDeletePanel({
  title, subtitle, columns, fetchList, deleteOne, deleteBulk,
  showFilters = true, testid = "evrepo", rowLabel = (i) => i.user_name || i.user_email || "este registro",
}) {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const superAdmin = isSuperAdmin();

  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({ total: 0, pages: 1, paginated: false });

  const load = (f = filter, p = page) => {
    setLoading(true);
    Promise.resolve(fetchList(showFilters ? f : undefined, p))
      .then((res) => {
        if (Array.isArray(res)) {
          setItems(res);
          setPageInfo({ total: res.length, pages: 1, paginated: false });
        } else {
          setItems(res.items || []);
          setPageInfo({ total: res.total || 0, pages: res.pages || 1, paginated: true });
        }
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(filter, page); /* eslint-disable-next-line */ }, [filter, page]);

  const changeFilter = (key) => { setFilter(key); setPage(1); };

  const delOne = async (item) => {
    if (!window.confirm(`¿Eliminar el registro de ${rowLabel(item)}? Libera espacio y NO afecta sus puntos.`)) return;
    try { await deleteOne(item.id); toast.success("Eliminado. Los puntos del usuario no se modificaron."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const delBulk = async () => {
    setDeleting(true);
    try {
      const payload = (showFilters && filter !== "all") ? { all: true, status: filter } : { all: true };
      const r = await deleteBulk(payload);
      toast.success(`${r.deleted} registro(s) eliminado(s). Los puntos de los usuarios no se modificaron.`);
      setConfirmBulk(false); setPage(1); load(filter, 1);
    } catch (e) { toast.error(apiError(e)); } finally { setDeleting(false); }
  };

  const colCount = columns.length + (superAdmin ? 1 : 0);

  return (
    <div data-testid={`${testid}-panel`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-extrabold text-[#0A1C30]">{title}</h2>
          {subtitle && <p className="text-sm text-[#6B7688] mt-0.5">{subtitle}</p>}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {showFilters && DEFAULT_FILTERS.map((f) => (
          <button key={f.key} data-testid={`${testid}-filter-${f.key}`} onClick={() => changeFilter(f.key)}
            className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${filter === f.key ? "bg-[#0A1C30] text-white" : "bg-white text-[#0A1C30] ring-1 ring-black/5 hover:ring-[var(--gold)]"}`}>
            {f.label}
          </button>
        ))}
        {superAdmin && items.length > 0 && (
          <button data-testid={`${testid}-delete-all`} onClick={() => setConfirmBulk(true)}
            className="ml-auto inline-flex items-center gap-2 rounded-full bg-[#FCE4E4] text-[#C0392B] font-bold px-4 py-2 text-sm hover:bg-[#f7d3d3] transition-colors">
            <Trash2 className="w-4 h-4" /> Eliminar {showFilters && filter !== "all" ? "las de esta vista" : "todas"}
          </button>
        )}
      </div>

      <div className="mt-4 bg-white rounded-2xl ring-1 ring-black/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-black/5 bg-[#FAFAFA]">
                {columns.map((c) => (
                  <th key={c.label} className="px-4 py-3.5 text-xs font-extrabold tracking-wide text-[#6B7688] uppercase whitespace-nowrap">{c.label}</th>
                ))}
                {superAdmin && <th className="px-4 py-3.5 text-right text-xs font-extrabold tracking-wide text-[#6B7688] uppercase">Eliminar</th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={colCount} className="px-4 py-10 text-center text-[#6B7688]">Cargando...</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={colCount} className="px-4 py-10 text-center text-[#6B7688]">Sin registros.</td></tr>}
              {!loading && items.map((it) => (
                <tr key={it.id} className="border-b border-black/5 last:border-0 hover:bg-[#FAFAFA]">
                  {columns.map((c) => (
                    <td key={c.label} className="px-4 py-4 text-sm text-[#2A3446] align-top">{c.render(it)}</td>
                  ))}
                  {superAdmin && (
                    <td className="px-4 py-4 text-right">
                      <button data-testid={`${testid}-del-${it.id}`} onClick={() => delOne(it)} title="Eliminar" className="inline-flex items-center justify-center rounded-lg bg-[#FCE4E4] text-[#C0392B] p-2 hover:bg-[#f7d3d3] transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {pageInfo.paginated && pageInfo.pages > 1 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-[#6B7688]" data-testid={`${testid}-page-info`}>Página {page} de {pageInfo.pages} · {pageInfo.total} registros</p>
          <div className="flex gap-2">
            <button data-testid={`${testid}-prev`} disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-lg bg-white ring-1 ring-black/5 text-[#0A1C30] font-bold px-4 py-2 text-sm disabled:opacity-40">Anterior</button>
            <button data-testid={`${testid}-next`} disabled={page >= pageInfo.pages || loading} onClick={() => setPage((p) => Math.min(pageInfo.pages, p + 1))} className="rounded-lg bg-[#0A1C30] text-white font-bold px-4 py-2 text-sm disabled:opacity-40">Siguiente</button>
          </div>
        </div>
      )}
      {!superAdmin && (
        <p className="mt-3 text-xs text-[#9AA3B2]">Solo el Super Admin puede eliminar registros. Aquí puedes consultarlos.</p>
      )}

      <Dialog open={confirmBulk} onOpenChange={(o) => !o && setConfirmBulk(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-xl font-extrabold text-[#0A1C30]">Eliminar registros</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Confirmar la eliminación de registros sin afectar los puntos de los usuarios.</DialogDescription>
          <p className="text-sm text-[#2A3446]">
            Se eliminarán <b>{showFilters && filter !== "all" ? `los registros con estado "${DEFAULT_FILTERS.find((f) => f.key === filter)?.label}"` : "TODOS los registros"}</b> ({pageInfo.total} en total). Libera espacio y <b>no afecta los puntos de graduación ni las medallas</b> de ningún usuario.
          </p>
          <DialogFooter className="gap-2">
            <button onClick={() => setConfirmBulk(false)} className="rounded-xl bg-[#F4F6F9] text-[#0A1C30] font-bold px-5 py-2.5">Cancelar</button>
            <button data-testid={`${testid}-delete-all-confirm`} onClick={delBulk} disabled={deleting} className="inline-flex items-center gap-2 rounded-xl bg-[#C0392B] text-white font-extrabold px-5 py-2.5 disabled:opacity-60">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Eliminar definitivamente
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
